import { useState, useEffect } from "react"
import { Modal, Pressable, View } from "react-native"
import { Text } from "../core"

interface AlertButton {
  text?: string
  onPress?: (value?: string) => void
  style?: "default" | "cancel" | "destructive"
}

interface AlertOptions {
  cancelable?: boolean
  onDismiss?: () => void
}

interface AlertState {
  title: string
  message?: string
  buttons: AlertButton[]
  options?: AlertOptions
}

type AlertListener = (state: AlertState | null) => void

let listener: AlertListener | null = null

function emit(state: AlertState | null) {
  listener?.(state)
}

export const Alert = {
  alert(title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) {
    const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: "OK", style: "cancel" as const }]
    emit({ title, message, buttons: resolvedButtons, options })
  },
}

function resolveButtonStyle(button: AlertButton, index: number, total: number) {
  if (button.style === "destructive") return "bg-red-500 active:bg-red-600" as const
  if (button.style === "cancel") return "bg-slate-100 active:bg-slate-200" as const
  // Last button in multi-button layout is the primary action
  if (total > 1 && index === total - 1) return "bg-blue-600 active:bg-blue-700" as const
  return "bg-slate-100 active:bg-slate-200" as const
}

function resolveTextColor(button: AlertButton, index: number, total: number) {
  if (button.style === "destructive") return "text-white"
  if (button.style === "cancel") return "text-slate-700"
  if (total > 1 && index === total - 1) return "text-white"
  return "text-slate-700"
}

export function AlertContainer() {
  const [state, setState] = useState<AlertState | null>(null)

  useEffect(() => {
    listener = setState
    return () => {
      listener = null
    }
  }, [])

  const handlePress = (button: AlertButton) => {
    button.onPress?.()
    setState(null)
  }

  const handleClose = () => {
    if (state?.options?.cancelable === false) return
    state?.options?.onDismiss?.()
    const cancelButton = state?.buttons.find((b) => b.style === "cancel" || b.text?.toLowerCase() === "cancel" || b.text === "إلغاء")
    cancelButton?.onPress?.()
    setState(null)
  }

  if (!state) return null

  return (
    <Modal visible transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable className="absolute inset-0 bg-black/40" onPress={handleClose} />
      <View className="flex-1 justify-center items-center px-6" style={{ pointerEvents: "box-none" }}>
        <View className="bg-white w-full max-w-[320px] rounded p-4">
          <Text className={`text-[16px] font-semibold text-slate-900 text-center ${state.message ? "mb-1" : "mb-4"}`}>{state.title}</Text>
          {!!state.message && <Text className="text-[13px] text-slate-500 text-center mb-4 leading-relaxed">{state.message}</Text>}
          <View className={state.buttons.length > 2 ? "flex-col w-full gap-2" : "flex-row gap-2 w-full"}>
            {state.buttons.map((button, index) => (
              <Pressable
                key={index.toString()}
                className={`flex-1 py-2.5 rounded-sm items-center ${resolveButtonStyle(button, index, state.buttons.length)}`}
                onPress={() => handlePress(button)}
              >
                <Text className={`font-semibold text-[14px] ${resolveTextColor(button, index, state.buttons.length)}`}>{button.text}</Text>
              </Pressable>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  )
}
